import { Button, ButtonGroup, Colors, Spinner, Tag } from "@blueprintjs/core";
import isArray from "lodash/isArray";
import isEmpty from "lodash/isEmpty";
import { IFGCustomInputProps, useFGContext } from "nsitools-react";
import * as React from "react";
import { useQuery } from "react-query";
import styled from "styled-components";

import { InstitutionSelectorDialog } from ".";
import { ClearButton } from "../..";
import { InstitutionSelectorApi } from "../../../api";
import { ERoutes } from "../../../AppRouter";
import { useAuth } from "../../../contexts";
import { useApiService } from "../../../hooks";
import { FGReadOnlyInput } from "../FGReadOnlyInput";

const InputContainer = styled.div`
  padding: 0.4rem 0 0 0.6rem;
`;

const StyledTag = styled(Tag)`
  & a {
    color: ${Colors.WHITE};
  }
`;

const NamesContainer = styled.div`
  > * + * {
    margin-left: 5px;
  }
`;

export interface IInstitutionSelectorFieldProps extends Omit<IFGCustomInputProps, "children"> {
  parentInstitutionIds?: number[];
  disabled?: boolean;
  allowMultipleSelection?: boolean;
  ref?: React.Ref<HTMLInputElement>;
}

export const InstitutionSelectorField: React.FunctionComponent<IInstitutionSelectorFieldProps> = ({
  name,
  parentInstitutionIds = [],
  label = null,
  disabled = false,
  className,
  allowMultipleSelection = false,
  ref,
  ...fgCustomInputProps
}) => {
  const { hasPermission } = useAuth();
  const ctx = useFGContext();
  const api = useApiService(InstitutionSelectorApi);

  const hasValue = React.useMemo(
    () =>
      (allowMultipleSelection && !isEmpty(ctx?.formik?.values?.[name])) ||
      (!allowMultipleSelection && !!ctx?.formik?.values?.[name]),
    [allowMultipleSelection, ctx?.formik?.values, name]
  );
  const [selectorDialogOpen, setSelectorDialogOpen] = React.useState(false);
  const [currentSelectedInstitutionIds, setSelectedInstitutionIds] = React.useState<number[]>([]);

  const { data: displayNames, isFetching: loadingDisplayNames } = useQuery(
    ["institution-display-names", currentSelectedInstitutionIds],
    async () => api.institutionSelectorGetDisplayNames({ request_body: currentSelectedInstitutionIds })
  );

  const clearSelectedInstitution = React.useCallback(() => {
    ctx?.formik?.setFieldValue(name, allowMultipleSelection ? [] : null, true);
    setSelectedInstitutionIds([]);
  }, [allowMultipleSelection, ctx?.formik, name]);

  React.useEffect(() => {
    if (ctx?.formik?.values?.[name]) {
      const value = ctx?.formik?.values?.[name];
      setSelectedInstitutionIds(isArray(value) ? value : [value]);
    } else {
      setSelectedInstitutionIds([]);
    }
  }, [ctx?.formik?.values, name]);

  const onInstitutionsSelected = React.useCallback(
    (selectedInstitutionIds?: number[]) => {
      if (selectedInstitutionIds?.length > 0) {
        ctx?.formik?.setFieldTouched(name);
        ctx?.formik?.setFieldValue(
          name,
          allowMultipleSelection ? selectedInstitutionIds : selectedInstitutionIds[0],
          true
        );
        setSelectedInstitutionIds(selectedInstitutionIds);
      }
    },
    [allowMultipleSelection, ctx?.formik, name]
  );

  const selectorProps = React.useMemo(
    () => ({
      excludedInstitutionIds: parentInstitutionIds,
      allowMultipleSelection,
      preSelectedIds: currentSelectedInstitutionIds
    }),
    [allowMultipleSelection, parentInstitutionIds, currentSelectedInstitutionIds]
  );

  const openInstitutionSelector = React.useCallback(() => {
    setSelectorDialogOpen(true);
  }, []);

  const getEditRoute = React.useCallback(
    (id: number) =>
      `#${ERoutes.institution}/${id}/detail/${hasPermission("INSTITUTION", "RW", "DETAIL") ? "edit" : "view"}`,
    [hasPermission]
  );

  const removeInstitutionId = React.useCallback(
    (institutionId: number) => {
      const newInstitutionIds = [...currentSelectedInstitutionIds];
      newInstitutionIds.splice(currentSelectedInstitutionIds.indexOf(institutionId), 1);
      ctx?.formik?.setFieldTouched(name);
      ctx?.formik?.setFieldValue(name, newInstitutionIds, true);
      setSelectedInstitutionIds(newInstitutionIds);
    },
    [ctx?.formik, currentSelectedInstitutionIds, name]
  );

  const getInstitutionLink = React.useCallback(
    (id: number, name: string, tags: boolean) => {
      const link = hasPermission("INSTITUTION", "R", "DETAIL") ? (
        <a href={getEditRoute(id)} target="_blank" rel="noreferrer" tabIndex={-1} key={id}>
          {name}
        </a>
      ) : (
        name
      );
      return tags ? (
        <StyledTag key={id} onRemove={() => removeInstitutionId(id)}>
          {link}
        </StyledTag>
      ) : (
        link
      );
    },
    [getEditRoute, hasPermission, removeInstitutionId]
  );

  const onClosed = React.useCallback(
    (selectedInstitutionIds?: number[]) => {
      onInstitutionsSelected(selectedInstitutionIds);
      setSelectorDialogOpen(false);
    },
    [onInstitutionsSelected]
  );

  return (
    <>
      <FGReadOnlyInput
        name={name}
        label={label}
        leftElement={
          <InputContainer>
            {loadingDisplayNames ? (
              <Spinner size={16} />
            ) : (
              <NamesContainer>
                {displayNames.map(dn => getInstitutionLink(dn.id, dn.text, allowMultipleSelection))}
              </NamesContainer>
            )}
          </InputContainer>
        }
        rightElement={
          <ButtonGroup>
            {hasValue && <ClearButton onClick={clearSelectedInstitution} tabIndex={-1} disabled={disabled} />}
            <Button icon="people" minimal={true} onClick={openInstitutionSelector} disabled={disabled} tabIndex={-1} />
          </ButtonGroup>
        }
        {...fgCustomInputProps}
      />
      <InstitutionSelectorDialog
        onClosed={onClosed}
        onInstitutionSelected={onClosed}
        selectorDialogOpen={selectorDialogOpen}
        {...selectorProps}
      />
    </>
  );
};
